import { GuestPreferenceStatModel } from 'api-hooks/guest';
import { Value } from 'baseui/select';
import { PreferenceGroupSubTypeEnum } from 'common/constants/enum';
import { useFormState } from 'components/elements/form';
import deleteColumn from 'components/elements/table/editable-columns/delete-column';
import TableInput from 'components/elements/table/table-input';
import {
  ActionType,
  ColumnDataType,
  IColumn,
} from 'components/elements/table/type';
import useGetGuestColumn from 'hooks/use-get-guest-column';
import useGetMultiplePreferenceColumn from 'hooks/use-get-multiple-preference-column';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { HotelSaleGuestItemFormType, HotelSaleFormType } from '../form-type';
import { addNewGuestItem } from '../hooks/use-get-default-values';

interface Props {
  parentIndex: number;
}

function tagTransformer(item: GuestPreferenceStatModel) {
  return {
    id: item.id,
    label: `${item.name} ${item.count > 1 ? `(X${item.count})` : ''}`,
  };
}

function useGetScheduleGuestTableList(): IColumn<HotelSaleGuestItemFormType>[] {
  const { t } = useTranslation();
  const { editable } = useFormState();
  const guestColumn = useGetGuestColumn();
  const multiplePreferenceColumn = useGetMultiplePreferenceColumn({
    subType: PreferenceGroupSubTypeEnum.Hotel,
  });

  return [
    {
      header: t('common:lead_guest'),
      accessorKey: 'isLead',
      minSize: 88,
      size: 88,
      dataType: ColumnDataType.checkbox,
      disabled: true,
    },
    {
      header: t('common:guest'),
      accessorKey: 'guestId',
      minSize: 250,
      dataType: ColumnDataType.select,
      required: true,
      selectProps: guestColumn,
    },
    {
      header: t('common:guest_preferences'),
      accessorKey: 'guestPreferences',
      minSize: 300,
      dataType: ColumnDataType.tags,
      tagKeyName: 'label',
      maxTags: 2,
    },
    {
      header: t('guest:request_subject_to_availability'),
      accessorKey: 'preferences',
      minSize: 250,
      dataType: ColumnDataType.selectMulti,
      selectProps: multiplePreferenceColumn,
    },
    ...deleteColumn(editable),
  ];
}

export default function ScheduleGuestTable(props: Props) {
  const { editable } = useFormState();
  const { clearErrors, control, getValues, trigger } =
    useFormContext<HotelSaleFormType>();

  const name = `hotelSales.${props.parentIndex}.hotelSaleGuests` as const;

  const { fields, update, append, remove, replace } = useFieldArray({
    control,
    name,
    keyName: '__customId',
  });

  const columns = useGetScheduleGuestTableList();

  const dispatch = React.useCallback(
    (action) => {
      const guests = getValues(name);
      const currentRowIndex = action.rowIndex as number;
      const guest = guests[currentRowIndex];
      const type = action.type as ActionType;
      const columnId = action.columnId as keyof HotelSaleGuestItemFormType;
      const value = action.value;
      const extra = action.extra as Value;

      const otherVal: () => Partial<HotelSaleGuestItemFormType> = () => {
        if (columnId === 'guestId') {
          const allPreference = extra?.[0]
            ? [
                ...(extra?.[0].extra.preferences || []),
                ...(extra?.[0].extra.preferenceStats || []),
              ]
            : [];

          return {
            guestPreferences: (allPreference || []).map((item) =>
              tagTransformer(item),
            ),
            guestName: extra?.[0]?.label as string,
          };
        }
        return {};
      };

      if (type === 'addRow') {
        append(addNewGuestItem());
        trigger(name);
      } else if (type === 'removeRow') {
        remove(currentRowIndex);
        //
        const guests = getValues(name);
        guests[0].isLead = true;
        replace(guests);
        //
        trigger(name);
      } else if (type === 'update_cell') {
        update(currentRowIndex, {
          ...guest,
          [columnId]: value,
          ...otherVal(),
        });
        clearErrors(`${name}.${currentRowIndex}.${columnId}`);
      }
    },
    [append, clearErrors, getValues, name, remove, replace, trigger, update],
  );

  return (
    <div style={{ width: '100%' }}>
      <TableInput
        columns={columns}
        data={fields}
        dispatch={dispatch}
        disabled={!editable}
        name={name}
        uniqueRowKey="customId"
      />
    </div>
  );
}
